import {
  Button, Form, DatePicker, message, Upload, UploadProps
} from "antd";
import { localUploadBaseProps } from "../../../utils/localUploadRequest";
import dayjs from "dayjs";
import fileDownload from "js-file-download";
import { useState, useEffect } from "react";
import { Dispatch, SetStateAction } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { useReactiveVar } from "@apollo/client";
import "./InternetSourceCost.scss";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import ContentTitle from "../../../components/ui/ContentTitle";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { useUpdateInternetCostMutation } from "../../../graphql/operations/mkt/update-internet-cost";
import {
  __currentUser__,
  __jwtToken__,
  __projectKey__,
} from "../../../graphql/policies";
import { ServiceTypes } from "../../../types/types";
import { apiCall } from "../../../utils/apiCall";
import requiredRule from "../../../utils/formRules/requiredRule";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import { renderDecodedId } from "../../../utils/renderDecodedId";

interface InternetSourceCostProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const InternetSourceCost = ({
  setSelectedService,
  setSelectSubItem,
}: InternetSourceCostProps) => {
  const [form] = Form.useForm();

  const [isCostLoading, setIsCostLoading] = useState(false);
  const [isReportLoading, setIsReportLoading] = useState(false);

  const currentUser = useReactiveVar(__currentUser__);
  const jwtToken = useReactiveVar(__jwtToken__);
  const projectKey = useReactiveVar(__projectKey__);

  const [updateInternetCost] = useUpdateInternetCostMutation();
  const { data: userPrograms, loading: userProgramsLoading } =
    useGetUserProgramsByServiceQuery({
      variables: {
        userId: parseInt(renderDecodedId(currentUser?.id)),
        serviceId: 4,
      },
    });

  useEffect(() => {
    setSelectedService(ServiceTypes.MKT);
    setSelectSubItem("Internet Source Cost");
  }, []);

  const props: UploadProps = {
    ...localUploadBaseProps,

    maxCount: 1,
    beforeUpload: (file) => {
      const isXlsx = file.name.toLowerCase().endsWith('xlsx');

      if (!isXlsx) {
        message.error(renderAntDMessageConfig("Only upload xlsx file!"));
      }

      return isXlsx || Upload.LIST_IGNORE;
    }
  };

  const handleReportSubmit = () => {
    form.setFields([{ name: 'file', errors: [] }]);
    form
      .validateFields(['yyyymm'])
      .then(async () => {
        setIsReportLoading(true);
        const { yyyymm } = form.getFieldsValue();
        const fileName = "source_cost-" + dayjs(yyyymm).format("YYYYMM");
        const header = {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "X-Project-Id": "AF",
            "X-Authorization-Provider": "Anonymous",
            "X-Project-Key": projectKey,
          },
          responseType: "blob",
        };
        const data = await apiCall({
          method: "GET",
          header: header,
          endPoint:
            process.env.REACT_APP_GET_INTERNET_SOURCE_COST +
            fileName +
            "/" +
            dayjs(yyyymm).format("YYYY-MM")
        });
        fileDownload(data, fileName + ".xlsx");

        setIsReportLoading(false);
      })
      .catch((e) => console.log(e));
  };

  const handleCostSubmit = () => {
    form
      .validateFields(['yyyymm', 'file'])
      .then(async () => {
        setIsCostLoading(true);
        const { yyyymm, file } = form.getFieldsValue();

        await updateInternetCost({
          variables: {
            yyyymm: dayjs(yyyymm).format("YYYY/MM"),
            file: file[0].originFileObj
          },
        }).then((res) => {
          const error = res?.data?.updateInternetCost?.error;
          if (error) {
            message.error("Import Internet Cost Failed");
          } else {
            message.success("Successfully");
          }
        })
          .catch((e) => console.log(e));

        setIsCostLoading(false);
      })
      .catch((e) => {
        console.log(e);
        message.error("Import Internet Cost Failed");
      });
  };

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Internet Source Cost"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="internetSourceCostWrapper">
      <ContentTitle title="匯入Internet Cost" />
      <div className="table-container">
        <Form form={form} name="ealm-bonus-form-report" className="form">
          <div className="flex">
            <Form.Item
              name="yyyymm"
              rules={[requiredRule]}
              label="Year month"
              className="item"
            >
              <DatePicker
                format="YYYY-MMM"
                picker="month"
                className="w-full"
                disabledDate={(current) => {
                  return (
                    current && current > dayjs().endOf("month").subtract(1, "month")
                  );
                }}
              />
            </Form.Item>
            <Button
              type="primary"
              className="ms-5"
              onClick={handleReportSubmit}
              loading={isReportLoading}
            >
              產生回函數量Excel
            </Button>
          </div>
          <div className="flex">
            <Form.Item
              name="file"
              rules={[requiredRule]}
              valuePropName="fileList"
              getValueFromEvent={(e) => e.fileList}
            >
              <Upload
                {...props}
              >
                <Button icon={<UploadOutlined />} style={{ width: 200 }} >
                  上傳Excel
                </Button>

              </Upload>
            </Form.Item>
            <Button
              type="primary"
              className="ms-5"
              onClick={handleCostSubmit}
              loading={isCostLoading}
            >
              匯入Cost
            </Button>
          </div>
        </Form>

        <Form form={form} name="ealm-bonus-form" className="form">

        </Form>
      </div>
    </div>
  );
};

export default InternetSourceCost;
